import { DayPlan } from "@/state/DayPlan";
import { ChecklistItem } from "@/state/ChecklistItem";
import { Task } from "@/state/Task";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Dropdown, Form, ListGroup, ListGroupItem, Modal, ProgressBar, Stack, Table } from "react-bootstrap";
import { format, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import { TaskInput, TaskInputButton } from "./TaskInput";
import { formatMins, formatMinsSimple, parseMins, totalTime, transparentize, deadlineColor } from "@/state/helpers";
import Icon from '../Icon';
import {
    faChevronDown as caretDown,
    faChevronUp as caretUp,
    faCheckCircle as checkmarkCircle,
    faClock as time,
    faTrash as trashBin,
    faCopy as copyOutline,
    faArrowRight as moveOutline,
    faUsers as peopleOutline,
    faStar as starOutline,
    faCheck as checkmarkDone,
    faCrosshairs as targetIcon,
    faArchive
} from '@fortawesome/free-solid-svg-icons';
import classNames from "classnames";
import { emptyPlan } from "@/state/reducer";
import { PlanScreenContext } from "./context";
import "./style.css";
import ChecklistInput from "../checklistInput";
import { useTaskModal } from "../TaskModalContext";
import { useData, useTaskChecklistItems, useTaskStats } from "@/storage/accessors";
import { Countable } from "@/state/common";
import { useScreenSize } from "@/contexts/screenSizeContext";
import { useReadonly } from "@/contexts/readonlyContext";
import { LifePart } from "@/state/LifePart";


type TaskTimeModalProps = {
    popup: boolean;
    toggle: () => void;
    plan: DayPlan;
    otherPlans: DayPlan[];
};

const SLOTS = [
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
]

const PERIODS = ["00:15", "00:30", "01:00", "01:30", "02:00"]

function TaskTimeModal({ popup, toggle, plan, otherPlans }: TaskTimeModalProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [startTime, setStartTime] = useState(plan.startTime || "");
    const [endTime, setEndTime] = useState(plan.endTime || "");
    const [error, setError] = useState(false);

    const { tasks, plans } = useData();

    const task = tasks.objects[plan.task];

    const saveForm = () => {
        if (startTime === "" || endTime === "") {
            setError(true);
            return;
        }
        if (endTime < startTime) {
            setError(true);
            return;
        }
        setError(false);
        plans.save(plan.uuid, { startTime, endTime });
        toggle();
    };

    const noTime = () => {
        setError(false);
        plans.save(plan.uuid, { startTime: "", endTime: "" });
        toggle();
    };

    useEffect(() => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 100);
    }, []);

    return (
        <Modal show={popup} onHide={toggle}>
            <Modal.Header className="text-bg-light" closeButton>
                <div>Задача &quot;{task.description}&quot;<br />
                    <small className="text-secondary">Дата: {format(parse(plan.date, 'yyyy-MM-dd', new Date()), "dd MMM", { locale: ru })}</small>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={saveForm}>
                    <Form.Group className="mb-2">
                        <Form.Label>Время начала</Form.Label>
                        <Form.Control isInvalid={error && startTime === ""} type="time" size="sm" value={startTime} onChange={e => setStartTime(e.target.value)} ref={inputRef} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Время окончания</Form.Label>
                        <Form.Control isInvalid={error && (endTime === "" || (startTime !== "" && endTime < startTime))} type="time" size="sm" value={endTime} onChange={e => setEndTime(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Button variant="outline-primary" onClick={saveForm}>Сохранить</Button>{" "}
                        <Button variant="outline-secondary" onClick={noTime}>Убрать время</Button>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Table className="text-small m-0">
                <thead><tr><th>Начало</th><th>Длительность</th></tr></thead>
                <tbody>
                    {SLOTS.map(slot => <tr key={slot}>
                        <td>{slot}</td>
                        <td>{PERIODS.map(period => {
                            const newEndTime = formatMinsSimple(parseMins(slot) + parseMins(period));
                            const hasIntersections = otherPlans.filter(p => p.uuid !== plan.uuid && hasIntersection(p, { ...plan, startTime: slot, endTime: newEndTime })).length > 0;
                            // if (hasIntersections) return null;
                            return <Button variant={hasIntersections ? "danger" : "success"} className="me-1" size="sm" key={period}
                                onClick={() => {
                                    plans.save(plan.uuid, { startTime: slot, endTime: newEndTime });
                                    toggle();
                                }}>{formatMins(parseMins(period))}</Button>
                        })}</td>
                    </tr>)}
                </tbody>
            </Table>
        </Modal>
    );
}

const hasIntersection = (a: DayPlan, b: DayPlan) => {
    if (!a.startTime || !b.startTime) return false;
    const aStart = parseMins(a.startTime);
    const aEnd = parseMins(a.endTime || "23:59");
    const bStart = parseMins(b.startTime);
    const bEnd = parseMins(b.endTime || "23:59");
    return (bStart <= aStart && aStart < bEnd) || (aStart <= bStart && bStart < aEnd);
};


const isDelayedTaskActive = (plan: DayPlan): boolean => {
    if (!plan.delayedTill) return false;

    const now = new Date();
    const currentTime = format(now, 'HH:mm');

    // Если время откладывания еще не наступило, задача активна
    return currentTime < plan.delayedTill;
};

const DayTaskChecklist: React.FC<{ task: Task, date: Date, openInnerTask: (taskUuid: string) => void }> = ({ task, date, openInnerTask }) => {
    const checklistItems = useTaskChecklistItems(task.uuid)
    const { checklistItems: storage } = useData();
    const { readonly } = useReadonly();
    const toggleCheck = (item: ChecklistItem) => () => {
        if (readonly) return;
        const isDone = !item.isDone;
        storage.save(item.uuid, { isDone: isDone, dateDone: isDone ? format(date, "yyyy-MM-dd") : "" })
    };

    const newTaskNumber = Math.max(...checklistItems.map(cl => cl.order || 0), 0) + 1;


    return <>
        {checklistItems.filter(t => (!t.isDone || t.dateDone === format(date, 'yyyy-MM-dd'))).map(item => (
            <ListGroupItem key={item.uuid} className="pt-0 pb-0">
                <Form.Group>
                    <Stack direction="horizontal" gap={2} style={{ wordBreak: "break-word" }}>
                        <Form.Check id={`t-${item.uuid}`} disabled={item.innerTask !== undefined || readonly} type="checkbox" checked={item.isDone} onChange={toggleCheck(item)} />
                        <Form.Label htmlFor={`t-${item.uuid}`} className="d-inline-flex w-100 justify-content-between align-items-center">
                            <small onClick={item.innerTask ? () => openInnerTask(item.innerTask || "") : () => null} style={{ cursor: item.innerTask ? "pointer" : undefined }} className={classNames('', { "text-secondary text-decoration-line-through": item.isDone, 'text-decoration-underline': item.innerTask })}>{item.title}</small>
                            {item.needTime && parseMins(item.needTime) > 0 && <small className={classNames('', { "text-decoration-line-through": item.isDone })}><Badge bg={item.isDone ? "success" : "secondary"}>{formatMins(parseMins(item.needTime))}</Badge></small>}
                        </Form.Label>
                    </Stack>
                </Form.Group>
            </ListGroupItem>
        ))}
        <ListGroupItem>
            {!readonly && <ChecklistInput task={task} newNumber={newTaskNumber} />}
        </ListGroupItem>
    </>
}

interface DayTaskCardProps {
    plan: DayPlan;
    date: Date;
    otherDayPlans: DayPlan[];
}

export const DayTaskCard: React.FC<DayTaskCardProps> = ({ plan, date, otherDayPlans }) => {
    const planContext = useContext(PlanScreenContext);
    const taskModalContext = useTaskModal();
    const setPlanTaskModal = planContext?.setTaskModal;
    const setTaskModal = taskModalContext?.setTaskModal;
    const filterPart = planContext?.filterPart || '';
    const searchTerm = planContext?.searchTerm || '';
    const planJustDone = planContext?.planJustDone || (() => { });
    const { roles, tasks, plans } = useData();
    const isMobile = useScreenSize() === "MOBILE";
    const { readonly } = useReadonly();
    const [isOpen, setIsOpen] = useState(false);
    const [editedTime, setEditedTime] = useState<string | null>(null);
    const toggleTime = () => setEditedTime(null);

    // Drag & drop handlers
    const handleDragStart = (e: React.DragEvent) => {
        if (isMobile || readonly) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/plain', plan.uuid);
        // Запоминаем флаг копирования прямо в dataTransfer — надёжнее чем глобальный ref,
        // т.к. keyup может сработать до drop
        if (e.ctrlKey || e.metaKey) {
            e.dataTransfer.setData('text/copy', 'true');
        }
        e.dataTransfer.effectAllowed = 'copyMove';
        // Add a custom drag image or styling if needed
        (e.target as HTMLElement).style.opacity = '0.5';
    };

    const handleDragEnd = (e: React.DragEvent) => {
        (e.target as HTMLElement).style.opacity = '1';
    };

    const openTaskModal = (taskUuid: string, fromDate?: Date) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: taskUuid, fromDate });
        } else if (setPlanTaskModal) {
            setPlanTaskModal({ taskUUID: taskUuid, fromDate });
        }
    };

    let task = tasks.objects[plan.task];
    if (task === undefined) {
        task = {
            description: "Удаленная задача",
            lifepart: Object.values(roles.objects)[0].uuid,
            uuid: crypto.randomUUID(),
            context: "",
            isDone: false
        }
    }
    const part = roles.objects[task.lifepart];

    // Находим самую верхнюю родительскую задачу
    const topParentTask = useMemo(() => {
        let currentTask = task;
        let topParent = null;

        while (currentTask && currentTask.parentTask) {
            const parentTask = tasks.objects[currentTask.parentTask];
            if (parentTask) {
                topParent = parentTask;
                currentTask = parentTask;
            } else {
                break;
            }
        }
        return topParent;
    }, [task.uuid, tasks.objects]);

    const otherPlansData = otherDayPlans.filter((p) => p !== undefined);
    const intersection = otherPlansData.filter(p => p.uuid !== plan.uuid).filter(p => hasIntersection(plan, p));
    const [isRunning, setIsRunning] = useState(false);

    // Получаем stats через useCallback для избежания лишних рендеров
    const checklistItems = useTaskChecklistItems(task?.uuid)
    const stats = useTaskStats(task || null, checklistItems, date);

    const needTime = totalTime(checklistItems.filter(i => !i.isDone) as Countable[]);

    const copyToDate = (targetDate: Date | null) => {
        if (readonly || !targetDate) return;
        const newPlan = emptyPlan(task, targetDate, 0);
        plans.create({ ...newPlan, needTime: plan.needTime, isImportant: plan.isImportant, isGoal: plan.isGoal, startTime: plan.startTime, endTime: plan.endTime })
        tasks.save(task.uuid, { isDone: false });
    };

    const moveToDate = (targetDate: Date | null) => {
        if (readonly || !targetDate) return;
        plans.save(plan.uuid, { date: format(targetDate, "yyyy-MM-dd") });
        tasks.save(task.uuid, { isDone: false });
    };

    const openInnerTask = (uuid: string) => {
        openTaskModal(uuid, date);
    };

    useEffect(() => {
        const currentDate = new Date();
        if (format(date, 'yyyy-MM-dd') !== format(currentDate, 'yyyy-MM-dd') || !plan.startTime || !plan.endTime) {
            setIsRunning(false);
            return;
        }
        const checkFunction = () => {
            const currentDate = new Date();
            const currentDateStr = format(currentDate, 'yyyy-MM-dd');
            const startDate = parse(`${currentDateStr} ${plan.startTime}`, 'yyyy-MM-dd HH:mm', new Date());
            const endDate = parse(`${currentDateStr} ${plan.endTime}`, 'yyyy-MM-dd HH:mm', new Date());
            setIsRunning(currentDate >= startDate && currentDate < endDate);
        };
        checkFunction();
        const interval = setInterval(checkFunction, 60 * 1000);
        return () => clearInterval(interval);
    }, [plan.startTime, plan.endTime, date]);

    useEffect(() => {
        if (!plan.createdAt) {
            plans.save(plan.uuid, { createdAt: format(new Date(), 'yyyy-MM-dd') })
        }
    }, [plan.uuid])

    const partColor = part?.color || "#FFFFFF";

    if (filterPart !== "" && part?.uuid !== filterPart) return null;
    if (searchTerm !== "" && (task.description || "").toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) < 0) return null;

    return (
        <>
            <li
                key={`goal-${task.uuid}`}
                className="list-group-item ps-0 pe-0 pb-0 mb-1"
                style={{ borderBottom: 0 }}

            >
                <Card draggable={!isMobile && !readonly}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd} className={classNames("mx-2",
                        { "text-bg-success": plan.isDone, 'border-success': plan.isDone, 'border-primary': isRunning, 'text-bg-info': isRunning },
                        { 'opacity-50': !plan.isDone && task.isDone }
                    )}>
                    <Card.Header className="pb-2 pt-3"
                        style={{
                            backgroundColor: transparentize(partColor, 0.85),
                            // padding: '0.4rem 0.6rem',
                            // borderRadius: '0.25rem',
                            cursor: 'pointer',
                            // fontSize: '0.95rem',
                            // marginBottom: '0.5rem'
                        }}

                    >
                        <Card.Title
                            style={{
                                fontSize: '0.95rem',
                            }}
                            className={classNames({ 'fw-bold': plan.isImportant })}
                            onClick={() => openTaskModal(task.uuid, date)}
                        >
                            {task.forPoint && <Icon icon={targetIcon} size="sm" className="me-1" style={{ color: partColor }} />}
                            {task.description}
                        </Card.Title>
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                            {plan.startTime && plan.endTime && (
                                <Badge style={{ cursor: readonly ? "default" : "pointer" }} onClick={() => !readonly && setEditedTime(plan.uuid)} bg={intersection.length > 0 ? "danger" : isRunning ? "primary" : "dark"}>
                                    <small>{plan.startTime} - {plan.endTime}</small>
                                </Badge>
                            )}
                            {plan.delayedTill && isDelayedTaskActive(plan) && (
                                <Badge bg="warning" text="dark">
                                    <small>отложено до {plan.delayedTill}</small>
                                </Badge>
                            )}
                            {task.deadline && !task.isDone && (
                                <Badge bg={deadlineColor(task.deadline)}>
                                    <small>Дедлайн: {format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}</small>
                                </Badge>
                            )}
                            {needTime.minutes > 0 && <Badge bg="secondary"><small>{needTime.asString}</small></Badge>}
                            {plan.isImportant && <Badge bg="danger"><Icon icon={peopleOutline} size="sm" /></Badge>}
                            {plan.isGoal && <Badge bg="warning" text="dark"><Icon icon={starOutline} size="sm" /></Badge>}
                            {topParentTask && <Badge bg="secondary" style={{ cursor: "pointer", maxWidth: 250, display: 'inline-block' }} onClick={() => openTaskModal(topParentTask.uuid, date)}>
                                <small style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topParentTask?.description || part?.name || 'Не назначено'}</small>
                            </Badge>}
                        </div>
                    </Card.Header>
                    <ListGroup variant="flush">
                        <ListGroup.Item variant={plan.isDone ? "success" : ""}>
                            <div className="d-flex align-items-center">
                                <div className="w-100">
                                    {stats.tasks > 0 && (
                                        <div className="mb-2 lh-1">
                                            <ProgressBar style={{ height: 10, cursor: "pointer" }} className="mt-1 mr-1 w-100" onClick={() => openTaskModal(task.uuid, date)}>
                                                <ProgressBar max={stats.tasks} now={stats.beforeFinishedTasks || 0} style={{ backgroundColor: partColor }} />
                                                <ProgressBar max={stats.tasks} now={stats.todayFinishedTasks || 0} style={{ backgroundColor: transparentize(partColor, 0.4) }} />
                                            </ProgressBar>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between">
                                        <ButtonGroup size="sm">
                                            <Button title="показать чеклист" variant={isOpen ? "secondary" : "outline-secondary"} size="sm" onClick={() => setIsOpen(old => !old)}>
                                                <Icon icon={isOpen ? caretUp : caretDown} size="sm" />
                                            </Button>
                                            {!plan.isDone && (
                                                <Button title="указать время" variant={parseMins(plan.needTime || "00:00") > 0 ? "outline-secondary" : "outline-secondary"} size="sm" style={{ cursor: "pointer" }} disabled={readonly} onClick={() => setEditedTime(plan.uuid)}>
                                                    <Icon icon={time} size="sm" />
                                                </Button>
                                            )}
                                        </ButtonGroup>
                                        <ButtonGroup size="sm">
                                            <Button title="готово" size="sm" variant={!plan.isDone ? "outline-secondary" : "success"} disabled={readonly} onClick={() => {
                                                const newState = !plan.isDone;
                                                plans.save(plan.uuid, { isDone: newState });
                                                if (newState) planJustDone(plan.uuid);
                                                if (!newState) {
                                                    tasks.save(task.uuid, { isDone: false });
                                                }
                                            }}>
                                                <Icon icon={checkmarkCircle} size="sm" />
                                            </Button>
                                            {!plan.isDone && (
                                                <Button title="это обязательство пред другими людьми" size="sm" variant={!plan.isImportant ? "outline-secondary" : "danger"} disabled={readonly} onClick={() => {
                                                    plans.save(plan.uuid, { isImportant: !plan?.isImportant });
                                                }}>
                                                    <Icon icon={peopleOutline} size="sm" />
                                                </Button>
                                            )}
                                            {!plan.isDone && (
                                                <Button title="это шаг к цели" size="sm" variant={!plan.isGoal ? "outline-secondary" : "warning"} disabled={readonly} onClick={() => {
                                                    plans.save(plan.uuid, { isGoal: !plan?.isGoal });
                                                }}>
                                                    <Icon icon={starOutline} size="sm" />
                                                </Button>
                                            )}
                                            {plan.isDone && (
                                                <Button title="Эта задача полностью завершена" size="sm" variant={!task.isDone ? "outline-secondary" : "success"} disabled={readonly} onClick={() => {
                                                    tasks.save(task.uuid, { isDone: !task.isDone });
                                                }}>
                                                    <Icon icon={faArchive} size="sm" />
                                                </Button>
                                            )}
                                            <Button title="удалить" variant="outline-secondary" disabled={readonly} onClick={() => plans.delete(plan.uuid)}>
                                                <Icon icon={trashBin} size="sm" />
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </div>
                            </div>
                        </ListGroup.Item>
                        {isOpen && <DayTaskChecklist task={task} date={date} openInnerTask={openInnerTask} />}
                    </ListGroup>
                </Card>
            </li>
            {editedTime && <TaskTimeModal plan={plan} toggle={toggleTime} popup otherPlans={otherPlansData} />}
        </>
    );
};

const timeCompare = (a: DayPlan, b: DayPlan) => {
    const aData = a;
    const bData = b;
    // @ts-ignore (values filtered before)
    const aMins = parseMins(aData.startTime);
    // @ts-ignore (values filtered before)
    const bMins = parseMins(bData.startTime);
    if (aMins !== bMins) return aMins - bMins;
    // @ts-ignore (values filtered before)
    const aEndMins = parseMins(aData.endTime);
    // @ts-ignore (values filtered before)
    const bEndMins = parseMins(bData.endTime);
    return aEndMins - bEndMins;
};

const createdAtCompare = (a: DayPlan, b: DayPlan) => {
    const aCr = a.createdAt ? parse(a.createdAt, "yyyy-MM-dd", new Date()) : new Date();
    const bCr = b.createdAt ? parse(b.createdAt, "yyyy-MM-dd", new Date()) : new Date();
    return aCr < bCr ? -1 : (a.createdAt == b.createdAt ? 0 : 1)
}

interface TaskListProps {
    header?: string
    date: Date,
    hideHeader?: boolean,
}

// Компонент заголовка сферы жизни
export const LifePartHeader: React.FC<{
    lifePart: LifePart;
    taskCount: number;
    isCollapsed: boolean;
    onToggle: () => void;
}> = ({ lifePart, taskCount, isCollapsed, onToggle }) => {
    return (
        <li className="list-group-item d-flex align-items-center py-2" style={{ backgroundColor: transparentize(lifePart.color, 0.9), borderBottom: `3px solid ${lifePart.color}` }}>
            <Button
                variant="link"
                size="sm"
                onClick={onToggle}
                className="p-0 me-2"
                style={{ color: 'inherit', textDecoration: 'none', minWidth: '20px' }}
            >
                <Icon icon={isCollapsed ? caretDown : caretUp} size="sm" />
            </Button>
            <div
                className="rounded me-2"
                style={{ width: 12, height: 12, backgroundColor: lifePart.color, flexShrink: 0 }}
            />
            <strong className="flex-grow-1">{lifePart.name}</strong>
            <Badge bg="secondary">{taskCount}</Badge>
        </li>
    );
};

// Пропсы для компонента группировки задач по сферам
export type LifePartGroupProps = {
    items: DayPlan[] | { lifepart: string; uuid: string; task?: string }[];
    getLifePartUuid: (item: DayPlan | { lifepart: string; uuid: string; task?: string }) => string;
    renderPlan?: (plan: DayPlan) => React.ReactNode;
    renderUnplanned?: (task: { lifepart: string; uuid: string; task?: string }) => React.ReactNode;
    roles: Record<string, LifePart>;
};

// Компонент для группировки задач по сферам жизни (переиспользуемый)
export const LifePartGroup: React.FC<LifePartGroupProps> = ({
    items,
    getLifePartUuid,
    renderPlan,
    renderUnplanned,
    roles,
}) => {
    // Инициализируем состояние лениво — это позволяет сохранять collapsed-состояние
    // в памяти компонента без localStorage или базы данных.
    // Состояние сохраняется пока компонент не размонтирован (не закрыта страница).
    const [collapsedParts, setCollapsedParts] = useState<Set<string>>(() => new Set());

    const togglePart = useCallback((partUuid: string) => {
        setCollapsedParts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(partUuid)) {
                newSet.delete(partUuid);
            } else {
                newSet.add(partUuid);
            }
            return newSet;
        });
    }, []);

    // Группировка задач по сферам жизни
    const itemsByLifePart = useMemo(() => {
        const groups: Record<string, (DayPlan | { lifepart: string; uuid: string; task?: string })[]> = {};

        items.forEach(item => {
            const lifepart = getLifePartUuid(item);
            if (!lifepart) return;

            if (!groups[lifepart]) {
                groups[lifepart] = [];
            }
            groups[lifepart].push(item);
        });

        return groups;
    }, [items, getLifePartUuid]);

    // Сортировка групп по order сфер жизни (как на странице Баланс)
    const sortedEntries = useMemo(() => {
        return Object.entries(itemsByLifePart).sort(([a], [b]) => {
            const orderA = roles[a]?.order ?? Infinity;
            const orderB = roles[b]?.order ?? Infinity;
            return orderA - orderB;
        });
    }, [itemsByLifePart, roles]);

    return (
        <>
            {sortedEntries.map(([lifepartUuid, items]) => {
                const lifePart = roles[lifepartUuid];
                if (!lifePart) return null;

                const isCollapsed = collapsedParts.has(lifepartUuid);

                return (
                    <React.Fragment key={lifepartUuid}>
                        <LifePartHeader
                            lifePart={lifePart}
                            taskCount={items.length}
                            isCollapsed={isCollapsed}
                            onToggle={() => togglePart(lifepartUuid)}
                        />
                        {!isCollapsed && items.map(item => {
                            if ('date' in item && renderPlan) {
                                return renderPlan(item as DayPlan);
                            } else if (!('date' in item) && renderUnplanned) {
                                return renderUnplanned(item as { lifepart: string; uuid: string; task?: string });
                            }
                            return null;
                        })}
                    </React.Fragment>
                );
            })}
        </>
    );
};

export const TaskList: React.FC<TaskListProps> = ({ header, date }) => {
    const { hideDone, justDonePlans } = useContext(PlanScreenContext);
    const isMobile = useScreenSize() === "MOBILE";
    const { readonly } = useReadonly();
    const [autoClearedTasks, setAutoClearedTasks] = useState<string[]>([]);
    const { plans, tasks, roles } = useData();

    const dayName = date ? format(date, 'EEEE', { locale: ru }) : "";
    const formattedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    const formattedDate = date ? format(date, 'dd.MM', { locale: ru }) : "";
    const dateStr = format(date, 'yyyy-MM-dd');

    // Таймер для автоматического снятия откладывания
    useEffect(() => {
        const checkDelayedTasks = () => {
            const now = new Date();
            const currentTime = format(now, 'HH:mm');

            // Находим все отложенные задачи, время которых уже наступило
            const delayedTasksToClear = Object.values(plans.objects).filter(plan =>
                plan.date === dateStr &&
                plan.delayedTill &&
                plan.delayedTill <= currentTime
            );

            // Очищаем delayedTill для просроченных задач
            const clearedTaskIds: string[] = [];
            delayedTasksToClear.forEach(plan => {
                plans.save(plan.uuid, { delayedTill: "" });
                clearedTaskIds.push(plan.uuid);
            });

            if (clearedTaskIds.length > 0) {
                setAutoClearedTasks(prev => [...prev, ...clearedTaskIds]);

                // Убираем уведомление через 5 секунд
                setTimeout(() => {
                    setAutoClearedTasks(prev => prev.filter(id => !clearedTaskIds.includes(id)));
                }, 5000);
            }
        };

        // Проверяем сразу при монтировании
        checkDelayedTasks();

        // Устанавливаем интервал проверки каждую минуту
        const interval = setInterval(checkDelayedTasks, 60000);

        return () => clearInterval(interval);
    }, [plans.objects, dateStr]);

    // Фильтруем dayPlans по дате
    const todayPlans = useMemo(() => {
        return Object.values(plans.objects).filter(p => p.date === dateStr)
    }, [plans, dateStr]);

    const visiblePlans = useMemo(() => {
        return hideDone
            ? todayPlans.filter(g => !g.isDone || justDonePlans.indexOf(g.uuid) >= 0)
            : todayPlans;
    }, [todayPlans, hideDone, justDonePlans])

    // Группировка задач по сферам жизни
    const plansByLifePart = useMemo(() => {
        const groups: Record<string, DayPlan[]> = {};

        visiblePlans.forEach(plan => {
            const task = tasks.objects[plan.task];
            if (!task) return;

            const lifepart = task.lifepart;
            if (!groups[lifepart]) {
                groups[lifepart] = [];
            }
            groups[lifepart].push(plan);
        });

        // Сортируем задачи внутри каждой группы
        Object.keys(groups).forEach(key => {
            groups[key].sort((a, b) => {
                // Сначала задачи со временем
                if (a.startTime && a.endTime && b.startTime && b.endTime) {
                    return timeCompare(a, b);
                }
                if (a.startTime && a.endTime) return -1;
                if (b.startTime && b.endTime) return 1;

                // Затем по важности и цели
                if (a.isImportant && a.isGoal && !b.isImportant && !b.isGoal) return -1;
                if (!a.isImportant && !a.isGoal && b.isImportant && b.isGoal) return 1;
                if (a.isImportant && !b.isImportant) return -1;
                if (!a.isImportant && b.isImportant) return 1;

                return createdAtCompare(a, b);
            });
        });

        return groups;
    }, [visiblePlans, tasks.objects]);

    const has_input = dateStr >= format(new Date(), 'yyyy-MM-dd');
    const finalTitle = formattedDay || header;
    const undoneCount = visiblePlans.length;

    // Цвет бейджа в зависимости от количества задач
    const taskCountBadgeVariant = useMemo(() => {
        if (undoneCount === 0) return "secondary";
        if (undoneCount <= 2) return "success";
        if (undoneCount <= 5) return "warning";
        return "danger";
    }, [undoneCount]);

    return (
        <>
            <ul className="list-group list-group-flush">
                {autoClearedTasks.length > 0 && (
                    <li key="auto-cleared-notification" className="list-group-item text-center bg-success text-white">
                        <small>
                            <Icon icon={checkmarkCircle} size="sm" className="me-1" />
                            Откладывание снято с {autoClearedTasks.length} задач
                        </small>
                    </li>
                )}
                <li key={`header-${finalTitle}`} className={classNames("list-group-item d-flex align-items-end", { 'sticky-top': !isMobile })}>
                    <div className="text-center w-100 d-flex flex-row justify-content-between">
                        <div><strong>{finalTitle}, {formattedDate}</strong></div>
                        <div className="d-flex align-items-center gap-1">
                            <Badge className="ms-1" bg={taskCountBadgeVariant}>{undoneCount}</Badge>
                            <TaskInputButton date={date} disabled={!has_input || readonly} />
                        </div>
                    </div>
                </li>

                {/* Задачи, сгруппированные по сферам жизни */}
                <LifePartGroup
                    items={visiblePlans}
                    getLifePartUuid={(plan) => tasks.objects[plan.task ?? '']?.lifepart || ''}
                    renderPlan={(plan) => (
                        <DayTaskCard date={date} plan={plan} key={plan.uuid} otherDayPlans={todayPlans} />
                    )}
                    roles={roles.objects}
                />

                {has_input && !readonly && <TaskInput key={`${date}`} date={date} />}
            </ul>
        </>
    );
};
